/**
 * PDF export for a "comparative material quote" on an order.
 * Shows an ordered table of candidate products with their computed total cost
 * at the given surface / thickness, plus already-locked materials and margin.
 */
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

function fmtEur(n) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return Number(n).toLocaleString("fr-FR", { style: "currency", currency: "EUR" });
}

/**
 * @param {Object} opts
 * @param {Object} opts.order { reference, client_name, order_type_name, amount_ht }
 * @param {number|null} opts.surface
 * @param {number|null} opts.thickness
 * @param {Array} opts.candidates  products sorted by cost asc (with total_cost field)
 * @param {Array} opts.lockedMaterials   already-locked lines (with product_name, supplier_name, surface_m2, thickness_mm, total_cost)
 * @param {Object} opts.summary  { total_materials_cost, amount_ht, material_margin, material_margin_pct }
 */
export function generateMaterialQuotePdf({ order, surface, thickness, candidates, lockedMaterials, summary }) {
  const pdf = new jsPDF({ unit: "mm", format: "a4" });

  pdf.setFontSize(16);
  pdf.setFont("helvetica", "bold");
  pdf.text("Devis matière comparatif", 14, 16);

  pdf.setFontSize(11);
  pdf.setFont("helvetica", "normal");
  const refLine = `${order?.reference || ""}${order?.client_name ? " — " + order.client_name : ""}`;
  pdf.text(refLine, 14, 24);

  pdf.setFontSize(9);
  let y = 30;
  if (order?.order_type_name) { pdf.text(`Type de commande : ${order.order_type_name}`, 14, y); y += 5; }
  if (order?.amount_ht != null) { pdf.text(`Montant HT commande : ${fmtEur(order.amount_ht)}`, 14, y); y += 5; }
  pdf.text(`Surface : ${surface != null && surface !== "" ? `${surface} m²` : "—"}   Épaisseur : ${thickness != null && thickness !== "" ? `${thickness} mm` : "—"}`, 14, y);
  y += 5;
  pdf.text(`Édité le ${new Date().toLocaleDateString("fr-FR")}`, 14, y);
  y += 4;

  // 1) Already-locked materials table
  if (lockedMaterials && lockedMaterials.length) {
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(11);
    y += 6;
    pdf.text("Matières verrouillées sur la commande", 14, y);
    y += 3;
    autoTable(pdf, {
      startY: y,
      styles: { fontSize: 8, cellPadding: 1.2 },
      headStyles: { fillColor: [99, 102, 241] },
      head: [["Fournisseur", "Produit", "Surface", "Épaisseur", "Coût total", "Notes"]],
      body: lockedMaterials.map(m => [
        m.supplier_name || "—",
        `${m.product_name || ""}${m.product_reference ? ` (${m.product_reference})` : ""}`,
        m.surface_m2 != null ? `${m.surface_m2} m²` : "—",
        m.thickness_mm != null ? `${m.thickness_mm} mm` : "—",
        fmtEur(m.total_cost),
        m.notes || "",
      ]),
    });
    y = pdf.lastAutoTable.finalY + 4;
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    pdf.text(`Coût matière total verrouillé : ${fmtEur(summary?.total_materials_cost || 0)}`, 14, y);
    y += 5;
    if (summary?.amount_ht) {
      const margin = summary.material_margin;
      const pct = summary.material_margin_pct;
      pdf.text(`Marge matière : ${fmtEur(margin)}  (${pct != null ? pct.toFixed(1) + "%" : "—"})`, 14, y);
      y += 5;
    }
  }

  // 2) Comparison table
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(11);
  y += 6;
  pdf.text("Candidats (tri par coût croissant)", 14, y);
  y += 3;
  autoTable(pdf, {
    startY: y,
    styles: { fontSize: 8, cellPadding: 1.2 },
    headStyles: { fillColor: [14, 165, 233] },
    head: [["#", "Fournisseur", "Produit", "Prix/unité réf.", "Prix/usage", "Épaisseur réf.", "Coût total", "% HT"]],
    body: (candidates || []).map((p, idx) => [
      idx === 0 && p.total_cost != null ? "★" : String(idx + 1),
      p.supplier_name || "—",
      `${p.name || ""}${p.reference ? ` (${p.reference})` : ""}`,
      p.price_per_reference_unit != null ? `${fmtEur(p.price_per_reference_unit)}/${p.reference_unit}` : "—",
      p.price_per_usage_unit != null && p.consumption_per_unit ? `${fmtEur(p.price_per_usage_unit)}/${p.consumption_per_unit}` : "—",
      p.consumption_reference_thickness_mm ? `${p.consumption_reference_thickness_mm} mm` : "—",
      p.total_cost != null ? fmtEur(p.total_cost) : "—",
      (p.total_cost != null && order?.amount_ht) ? `${(p.total_cost / order.amount_ht * 100).toFixed(1)}%` : "—",
    ]),
  });

  const fname = `devis-matiere-${(order?.reference || "commande").replace(/[^a-z0-9]/gi, "_")}-${new Date().toISOString().slice(0, 10)}.pdf`;
  pdf.save(fname);
}
